import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { processQueue } from './helpers'
import { API_URL, AppConstant } from '../../../../AppConstant'
import getTodayInUserTimezone from '../../../../utils/getTodayInUserTimezone'
import { APIStatus } from '../../../types'
import {
  CurrentPatient,
  TreatmentTracker,
  startChiropracticAsync,
  stopChiropracticAsync,
  undoVisitAsync,
  updateVisitFrequencyAsync,
} from '../currentPatient'

const mockDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
const MOCK_MODE = true

export interface User {
  id: string
  firstname: string
  lastname: string
  email: string | null
  phone: string
  dob: string
  unverifiedEmail: string | null
  unverifiedPhone: string | null
  createdAt: string
  updatedAt: string
  profilePic: string | null
}

export interface Patient {
  id: string
  createdAt: string
  updatedAt: string
  user: User
}

export interface VisitDetails {
  id: string
  appointmentTime: string
  needsFullSlot: boolean
  isVirtual: boolean
  isScreening: boolean
  status: string
  patientId: string
  clinicId: string
  chiropractorId: string | null
  bedId: string | null
  serviceStartTime: string | null
  serviceEndTime: string | null
  zoomLink: string | null
  isFirstBooking: boolean
  createdAt: string
  updatedAt: string
  patient: Patient
}

// export interface Visit {
//   checkinTime: string | null
//   bedId: string | null
//   queueStatus: string
//   createdAt: string
//   updatedAt: string
//   visitId: string
//   isNew: boolean
//   campaign: string | null
//   membershipStatus: string
//   subscriptionExpiresAt: string | null
//   scanDue: boolean
//   membership: boolean
//   visit: VisitDetails
// }

export interface VisitRecord {
  checkinTime: string | null
  bedId: string | null
  queueStatus: string
  createdAt: string
  updatedAt: string
  visitId: string
  isNew: boolean
  campaign: string | null
  membershipStatus: string
  subscriptionExpiresAt: string | null
  scanDue: boolean
  membership: boolean
  /** True while the member's pause is in flight; membershipStatus reads in-active then. */
  isPaused: boolean

  visit: {
    id: string
    appointmentTime: string | null
    needsFullSlot: boolean
    isVirtual: boolean
    isScreening: boolean
    status: string
    patientId: string
    clinicId: string
    chiropractorId: string | null
    bedId: string | null
    serviceStartTime: string | null
    serviceEndTime: string | null
    zoomLink: string | null
    isFirstBooking: boolean
    createdAt: string
    updatedAt: string

    patient: {
      id: string
      createdAt: string
      updatedAt: string

      user: {
        id: string
        firstname: string
        lastname: string
        email: string | null
        phone: string | null
        dob: string | null
        unverifiedEmail: string | null
        unverifiedPhone: string | null
        createdAt: string
        updatedAt: string
        profilePic: string | null
      }
    }
  }
}

export interface PriorityVisit {
  id: string
  name: string
}

interface queue {
  checkedIn: VisitRecord[]
  upcoming: VisitRecord[]
  previous: VisitRecord[]
}

// Scans interfaces
interface Scan {
  id: string
}

interface FirstScan {
  id: string
  userId: string
  scanScore: number | null
  visitId: string
  createdAt: string
  updatedAt: string
}

interface Scans {
  userScan: Scan[]
  firstScan: FirstScan
}

interface ScanReport {
  userScanId: string
  spineLevel: string
  leftValue: number
  rightValue: number
  status: string
  createdAt: string
  updatedAt: string
}

interface ScanReportDetails {
  userScanDatas: ScanReport[]
  fistSpinalSemgScan: ScanReport[]
  appointmentTime: string
  chiroProfilePic: string | null
  scanScore: number | null
  clinic?: {
    id: string
    name: string
  }
  nextScanDueIn: number
}

interface AllNotesId {
  id: string
}

interface NoteById {
  id: string
  visitId: string
  chiropractorId: string
  subjective: string
  objective: string
  assessment: string
  plan: string
  status: string
  emailSummary: string
  createdAt: string
  updatedAt: string
  visit: {
    id: string
    appointmentTime: string
    timezone: string
    status: string
    checkinTime: string | null
    serviceStartTime: string | null
    serviceEndTime: string | null
    zoomLink: string | null
    isFirstBooking: boolean
    needsFullSlot: boolean
    clinicId: string
    chiropractorId: string
    bedId: string | null
    patientId: string
    isScanBooking: boolean
    isVirtual: boolean
    screenerId: string | null
    k1Summary: string | null
    isScreening: boolean
    createdAt: string
    updatedAt: string
    clinic: {
      id: string
      name: string
      code: string
      slug: string
      market: string
      coordinates: string | null
      address: string
      country: string
      state: string
      studioEmail: string
      zipCode: string
      reviewLink: string | null
      mapLocation: string | null
      mapImage: string | null
      priority: number
      createdAt: string
      updatedAt: string
    }
  }
  chiropractor: {
    id: string
    clinicId: string
    isActive: boolean
    createdAt: string
    updatedAt: string
    user: {
      id: string
      firstname: string
      lastname: string | null
      signupThrough: string | null
      referralCode: string | null
      phone: string | null
      unverifiedPhone: string | null
      dob: string | null
      email: string | null
      unverifiedEmail: string | null
      externalUserId: string | null
      emailVerificationId: string | null
      welcomeEmailSend: boolean
      textConsent: boolean
      profilePic: string | null
      screeningStatus: string | null
      gender: string | null
      isDeleted: boolean
      createdAt: string
      updatedAt: string
    }
  }
}

export interface ResponseOption {
  id: string
  answerId: string
  optionId: string
  option: ResponseOptionDetail
  priority?: number
}

export interface ResponseOptionDetail {
  id: string
  text: string
  order: number
}

export interface LobbyState {
  priorityVisit: PriorityVisit | null
  currentVisit: VisitRecord | null
  callInNextVisitStatus: APIStatus
  callInNextVisitError: string | null

  // Date (YYYY-MM-DD, user's timezone) of the last full lobby refresh
  refreshDate: string | null

  queue: queue
  queueStatus: APIStatus
  queueError: string | null

  patientDetails: CurrentPatient | null
  patientDetailsStatus: APIStatus
  patientDetailsError: string | null

  // Polling state
  lastPollTimestamp: string | null
  pollLobbyQueueStatus: APIStatus
  pollLobbyQueueError: string | null

  //forceCheckInPatient
  forceCheckInPatient: any
  forceCheckInPatientStatus: APIStatus
  forceCheckInPatientError: string | null
  lobbyUndoVisitStatus: APIStatus
  lobbyUndoVisitError: string | null
  lobbyUndoVisit: any | null

  // Scans state
  scans: Scans | null
  scansStatus: APIStatus
  scanById: ScanReportDetails | null
  scanByIdStatus: APIStatus

  // Notes state
  getAllNotesId: AllNotesId[]
  getAllNotesIdStatus: APIStatus
  getAllNotesIdError: string | null
  noteById: NoteById | null
  noteByIdStatus: APIStatus
  noteByIdError: string | null

  // Priority answers state
  priorityAnswers: any | null
  priorityAnswersStatus: APIStatus
  priorityAnswersError: string | null

  // Patient notes state
  postNotes: any | null
  postNotesStatus: APIStatus
  postNotesError: string | null

  // Post treatment plan type state
  postTreatmentPlanType: any | null
  postTreatmentPlanTypeStatus: APIStatus
  postTreatmentPlanTypeError: string | null

  isResetOtpModalOpen: boolean
  tempOtpStatus: APIStatus
  tempOtpError: string | null
}

const initialState: LobbyState = {
  priorityVisit: null,
  currentVisit: null,
  callInNextVisitStatus: APIStatus.idle,
  callInNextVisitError: null,
  refreshDate: null,
  queue: { checkedIn: [], upcoming: [], previous: [] },
  queueStatus: APIStatus.idle,
  queueError: null,
  patientDetails: null,
  patientDetailsStatus: APIStatus.idle,
  patientDetailsError: null,
  lastPollTimestamp: null,
  pollLobbyQueueStatus: APIStatus.idle,
  pollLobbyQueueError: null,
  forceCheckInPatient: null,
  forceCheckInPatientStatus: APIStatus.idle,
  forceCheckInPatientError: null,
  lobbyUndoVisitStatus: APIStatus.idle,
  lobbyUndoVisitError: null,
  lobbyUndoVisit: null,
  // Scans state
  scans: null,
  scansStatus: APIStatus.idle,
  scanById: null,
  scanByIdStatus: APIStatus.idle,
  // Notes state
  getAllNotesId: [],
  getAllNotesIdStatus: APIStatus.idle,
  getAllNotesIdError: null,
  noteById: null,
  noteByIdStatus: APIStatus.idle,
  noteByIdError: null,
  // Priority answers state
  priorityAnswers: null,
  priorityAnswersStatus: APIStatus.idle,
  priorityAnswersError: null,
  // Patient notes state
  postNotes: null,
  postNotesStatus: APIStatus.idle,
  postNotesError: null,
  // Post treatment plan type state
  postTreatmentPlanType: null,
  postTreatmentPlanTypeStatus: APIStatus.idle,
  postTreatmentPlanTypeError: null,
  isResetOtpModalOpen: false,
  tempOtpStatus: APIStatus.idle,
  tempOtpError: null,
}

export const loadLobbyQueueAsync = createAsyncThunk<
  { data: VisitRecord[]; message: string }, // return type
  { clinicId: string },
  { rejectValue: string }
>('lobbyAuth/load', async ({ clinicId }, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    const response = await axios.get(`${API_URL}/clinic/${clinicId}/lobby`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue({
        data: response.data.data || [],
        message: response.data.message,
      })
    }

    return thunkAPI.rejectWithValue(response.data.message || 'Failed to fetch lobby patients')
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || 'Request failed')
  }
})

// export const pollLobbyQueueAsync = createAsyncThunk<
//   { data: any; message: string },
//   { clinicId: string; timeStamp: string },
//   { rejectValue: string }
// >('lobbyAuth/poll', async ({ clinicId, timeStamp }, thunkAPI) => {
//   try {
//     const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

//     const response = await axios.get(`${API_URL}/clinic/${clinicId}/lobby?startTime=${timeStamp}`, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
//       },
//     })

//     if (response.data?.code === 'success') {
//       return thunkAPI.fulfillWithValue({
//         data: response.data.data || [],
//         message: response.data.message,
//       })
//     }

//     return thunkAPI.rejectWithValue(response.data.message || 'Failed to fetch lobby patients')
//   } catch (err: any) {
//     return thunkAPI.rejectWithValue(err.response?.data?.message || 'Request failed')
//   }
// })

export const pollLobbyQueueAsync = createAsyncThunk<
  { data: any; message: string },
  { clinicId: string; timeStamp: string },
  { rejectValue: string }
>('lobbyAuth/poll', async ({ clinicId, timeStamp }, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    // Subtract 5 minutes from timestamp for API call to get last 5 minutes of deltas
    // If subtracting 5 minutes would result in a different date, use the original timestamp
    const originalDate = new Date(timeStamp)
    const subtractedDate = new Date(originalDate.getTime() - 5 * 60 * 1000)

    // Check if the date (day) has changed
    const originalDateStr = originalDate.toISOString().split('T')[0]
    const subtractedDateStr = subtractedDate.toISOString().split('T')[0]

    // Use original timestamp if date changed, otherwise use subtracted timestamp
    const apiTimestamp =
      originalDateStr !== subtractedDateStr ? originalDate.toISOString() : subtractedDate.toISOString()

    const response = await axios.get(`${API_URL}/clinic/${clinicId}/lobby?startTime=${apiTimestamp}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
      timeout: 10000, // 10 second timeout - CRITICAL FIX
    })

    if (response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue({
        data: response.data.data || [],
        message: response.data.message,
      })
    }

    return thunkAPI.rejectWithValue(response.data.message || 'Failed to fetch lobby patients')
  } catch (err: any) {
    // Better error logging
    if (err.code === 'ECONNABORTED') {
      console.error('[pollLobbyQueue] Request timeout after 10s')
      return thunkAPI.rejectWithValue('Request timeout')
    }

    if (err.code === 'ERR_NETWORK') {
      console.error('[pollLobbyQueue] Network error')
      return thunkAPI.rejectWithValue('Network error')
    }

    console.error('[pollLobbyQueue] Request failed:', err.message)
    return thunkAPI.rejectWithValue(err.response?.data?.message || 'Request failed')
  }
})

export const callInNextVisitAsync = createAsyncThunk(
  'lobby/callInNext',
  async (
    {
      queue,
      priorityVisit, // optional { id: visitId }
    }: {
      queue: any[]
      priorityVisit?: PriorityVisit | null
    },
    thunkAPI,
  ) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    let [nextVisit, ...restOfQueue] = queue

    if (priorityVisit && queue.length !== 0) {
      const index = queue.findIndex((q) => q.visitId === priorityVisit.id)

      if (index !== -1) {
        nextVisit = queue[index]
        restOfQueue = [queue[0], ...queue.slice(1, index), ...queue.slice(index + 1)]
      }
    }

    if (!nextVisit) {
      return thunkAPI.fulfillWithValue({
        nextVisit: null,
        queue: [],
      })
    }

    try {
      const response = await axios.post(
        `${API_URL}/clinic/called-in`,
        { visitId: nextVisit.visitId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )

      if (response.data?.code === 'success') {
        return thunkAPI.fulfillWithValue({
          nextVisit,
          queue: restOfQueue,
        })
      }

      if (response.data?.code === 'already_used_by_another_doctor') {
        return thunkAPI.rejectWithValue({
          error: 'calledInTwice',
          visitId: nextVisit.visitId,
          priorityVisitId: priorityVisit?.id,
          queue: restOfQueue,
        })
      }

      return thunkAPI.rejectWithValue(response.data.message)
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Request failed')
    }
  },
)

export const patientDetailsAsync = createAsyncThunk<
  CurrentPatient, // return type
  string, // argument: visitId
  { rejectValue: string }
>('lobby/patientDetails', async (visitId, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    const response = await axios.get(`${API_URL}/visit/${visitId}/portal`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    }

    return thunkAPI.rejectWithValue(response.data?.message || 'Failed to fetch patient')
  } catch (error: any) {
    const errMsg = error.response?.data?.message || error.message || 'Request failed'
    return thunkAPI.rejectWithValue(errMsg)
  }
})

export const forceCheckInPatientAsync = createAsyncThunk(
  'lobbyPortal/forceCheckInPatientAsync',
  async ({ visitId, clinicId }: { visitId: string; clinicId: string }, thunkAPI) => {
    //${process.env.REACT_APP_API_BASE_URL || '/api'
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .put(
        `${API_URL}/visit/forced-check-in`,
        { visitId: visitId, clinicId: clinicId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        visitId: visitId,
        response: response.data.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

//lobbyUndoVisitAsync

export const lobbyUndoVisitAsync = createAsyncThunk(
  'lobby/lobbyUndoVisitAsync',
  async ({ visitId }: { visitId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .post(
        `${API_URL}/visit/${visitId}/undo`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        visitId: visitId,
        response: response.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// Scans async thunks
export const getAllScanAsync = createAsyncThunk(
  'lobby/getAllScanAsync',
  async ({ patientId }: { patientId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/patient/${patientId}/scan`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const getScanByIdAsync = createAsyncThunk(
  'lobby/getScanByIdAsync',
  async ({ patientId, scanId }: { patientId: string; scanId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/patient/${patientId}/scan/${scanId}/data`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// Notes async thunks
export const getAllNotesIdAsync = createAsyncThunk(
  'lobby/getAllNotesIdAsync',
  async ({ patientId }: { patientId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/visit/patient/${patientId}/note-id`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        response: response.data.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const getNoteByIdAsync = createAsyncThunk(
  'lobby/getNoteByIdAsync',
  async ({ noteId }: { noteId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/patient/notes/${noteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        response: response.data.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// Priority answers async thunk
export const priorityAnswersAsync = createAsyncThunk(
  'lobby/priorityAnswersAsync',
  async ({ responseOptionId, priority }: { responseOptionId: string; priority: number }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .post(
        `${API_URL}/questionnaire/answers/priority`,
        { responseOptionId: responseOptionId, priority: priority },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        response: response.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// Patient notes async thunk
export const postPatientNotesAsync = createAsyncThunk(
  'lobby/postPatientNotesAsync',
  async ({ patientId, notes }: { patientId: string; notes: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .put(
        `${API_URL}/patient/${patientId}/notes`,
        {
          notes: notes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        patientId,
        notes: response.data.data?.notes || notes,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const createTempOtpAsync = createAsyncThunk<
  { message: string | null; created: boolean },
  string,
  { rejectValue: string }
>('lobby/createTempOtpAsync', async (patientId, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    const response = await axios.post(`${API_URL}/patient/${patientId}/temp-otp`, null, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue({
        message: response.data.message ?? null,
        created: response.data.data?.created ?? true,
      })
    }

    return thunkAPI.rejectWithValue(response.data?.message || 'Failed to reset OTP')
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to reset OTP')
  }
})

// Post treatment plan type async thunk
export const postTreatmentPlanType = createAsyncThunk(
  'lobby/postTreatmentPlanType',
  async ({ patientId, data, payload }: { patientId: string; data: any; payload: any }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    let response = await axios.post(`${API_URL}/patient/${patientId}/treatment-tracker`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(payload)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

const lobbyPortalSlice = createSlice({
  name: 'lobbyPortal',
  initialState,
  reducers: {
    // -----------------------------
    // UI STATE
    // -----------------------------
    setPriorityVisit(state, action: PayloadAction<PriorityVisit>) {
      state.priorityVisit = action.payload
    },
    setCurrentVisit(state, action: PayloadAction<VisitRecord | null>) {
      state.currentVisit = action.payload
    },

    resetPriorityVisit(state) {
      state.priorityVisit = null
    },

    resetLobbyState() {
      return { ...initialState }
    },

    // -----------------------------
    // ASYNC RESETTERS
    // -----------------------------
    clearLobby(state) {
      state.queue = { checkedIn: [], upcoming: [], previous: [] }
      state.queueError = null
      state.queueStatus = APIStatus.idle
    },

    resetLobbyStatus(state) {
      state.queueStatus = APIStatus.idle
    },

    resetLobbyError(state) {
      state.queueError = null
    },

    resetLobbyStatusAndError(state) {
      state.queueStatus = APIStatus.idle
      state.queueError = null
    },
    resetCallInNextVisitStatus(state) {
      state.callInNextVisitStatus = APIStatus.idle
    },
    setLastPollTimestamp(state, action: PayloadAction<string>) {
      state.lastPollTimestamp = action.payload
    },
    resetLastPollTimestamp(state) {
      state.lastPollTimestamp = null
    },
    setRefreshDate(state, action: PayloadAction<string | null>) {
      state.refreshDate = action.payload
    },
    resetForceCheckInPatientStatus(state) {
      state.forceCheckInPatientStatus = APIStatus.idle
    },
    openResetOtpModal(state) {
      state.isResetOtpModalOpen = true
      state.tempOtpStatus = APIStatus.idle
      state.tempOtpError = null
    },
    resetTempOtpStatus(state) {
      state.isResetOtpModalOpen = false
      state.tempOtpStatus = APIStatus.idle
      state.tempOtpError = null
    },
    resetPatientDetailsStatus(state) {
      state.patientDetailsStatus = APIStatus.idle
      state.patientDetailsError = null
    },
    setPatientNotes(state, action: PayloadAction<string>) {
      if (state.patientDetails?.patient) {
        state.patientDetails.patient.notes = action.payload
      }
    },
    updateResponseOptionPriority(state, action: PayloadAction<{ responseOptionId: string; priority: number }>) {
      if (state.patientDetails?.questionResponses) {
        const { responseOptionId, priority } = action.payload
        state.patientDetails.questionResponses.forEach((qr) => {
          qr.responseOptions.forEach((ro) => {
            if (ro.id === responseOptionId) {
              ro.priority = priority
            }
          })
        })
      }
    },
    clearResponseOptionPriority(state, action: PayloadAction<{ responseOptionId: string }>) {
      if (state.patientDetails?.questionResponses) {
        const { responseOptionId } = action.payload
        const clearedPriority =
          state.patientDetails.questionResponses
            .flatMap((qr) => qr.responseOptions)
            .find((ro) => ro.id === responseOptionId)?.priority || 0

        state.patientDetails.questionResponses.forEach((qr) => {
          qr.responseOptions.forEach((ro) => {
            if (ro.id === responseOptionId) {
              ro.priority = 0
            } else if (ro.priority !== undefined && ro.priority > clearedPriority && ro.priority > 0) {
              ro.priority = Math.max(ro.priority - 1, 0)
            }
          })
        })
      }
    },
    clearNoteById(state) {
      state.noteById = null
      state.noteByIdError = null
    },
    clearScans(state) {
      state.scans = null
      state.scanById = null
    },
    resetNotesListAndNoteById(state) {
      state.getAllNotesId = []
      state.getAllNotesIdStatus = APIStatus.idle
      state.getAllNotesIdError = null
      state.noteById = null
      state.noteByIdStatus = APIStatus.idle
      state.noteByIdError = null
    },
    updateTreatmentTracker(state, action: PayloadAction<TreatmentTracker>) {
      if (state.patientDetails) {
        state.patientDetails.treatmentTracker = action.payload
      }
    },
  },

  extraReducers: (builder) => {
    builder
      //loadLobbyQueueAsync
      .addCase(loadLobbyQueueAsync.pending, (state) => {
        state.queueStatus = APIStatus.pending
        state.queueError = null
      })
      .addCase(loadLobbyQueueAsync.fulfilled, (state, action) => {
        state.queueStatus = APIStatus.fulfilled

        let incoming = action.payload.data || []

        // 1. Find cancelled visits
        const cancelledVisitIds = incoming
          .filter((v: any) => v.visit?.status?.toLowerCase() === 'cancelled')
          .map((v: any) => v.visitId)

        // 2. Remove cancelled visit from upcoming list completely
        if (cancelledVisitIds.length > 0) {
          incoming = incoming.filter((item: any) => {
            const isCancelled = cancelledVisitIds.includes(item.visitId)
            const isUpcoming = item.queueStatus?.toLowerCase() === 'upcoming'

            // remove ONLY cancelled upcoming items
            return !(isCancelled && isUpcoming)
          })
        }

        // --- END SAME LOGIC ---

        state.queue = processQueue(incoming)
        state.queueError = null

        state.refreshDate = getTodayInUserTimezone()
        state.lastPollTimestamp = new Date().toISOString()
      })

      .addCase(loadLobbyQueueAsync.rejected, (state, action) => {
        state.queueStatus = APIStatus.rejected
        state.queueError = action.payload || 'Failed to fetch lobby queue'
      })

      //pollLobbyQueueAsync
      .addCase(pollLobbyQueueAsync.pending, (state, action) => {
        state.pollLobbyQueueStatus = APIStatus.pending
        // Store the timestamp we're polling with
        state.lastPollTimestamp = action.meta.arg.timeStamp
      })

      // .addCase(pollLobbyQueueAsync.fulfilled, (state, action) => {
      //   state.pollLobbyQueueStatus = APIStatus.fulfilled
      //   // Timestamp is already stored in pending case
      //   const newItems = action.payload.data || []

      //   if (!newItems.length) return

      //   // 1. flatten existing queue
      //   const existing = [...state.queue.checkedIn, ...state.queue.upcoming, ...state.queue.previous]

      //   // 2. merge without duplicates
      //   const mergedMap = new Map()

      //   existing.forEach((item: any) => mergedMap.set(item.visitId, item))
      //   newItems.forEach((item: any) => mergedMap.set(item.visitId, item))

      //   // 3. Re-run processing
      //   const mergedArray = Array.from(mergedMap.values())
      //   state.queue = processQueue(mergedArray)
      // })

      .addCase(pollLobbyQueueAsync.fulfilled, (state, action) => {
        state.pollLobbyQueueStatus = APIStatus.fulfilled

        const newItems = action.payload.data || []
        if (!newItems.length) return

        // 1. Flatten existing queue
        const existing = [...state.queue.checkedIn, ...state.queue.upcoming, ...state.queue.previous]

        // 2. Merge without duplicates (newItems override existing)
        const mergedMap = new Map()
        existing.forEach((item: any) => mergedMap.set(item.visitId, item))
        newItems.forEach((item: any) => mergedMap.set(item.visitId, item))

        let mergedArray = Array.from(mergedMap.values())

        // 3. Find cancelled visits
        const cancelledVisitIds = newItems
          .filter((v: any) => v.visit?.status?.toLowerCase() === 'cancelled')
          .map((v: any) => v.visitId)

        // 4. Remove cancelled visit from upcoming list completely
        if (cancelledVisitIds.length > 0) {
          mergedArray = mergedArray.filter((item: any) => {
            const isCancelled = cancelledVisitIds.includes(item.visitId)
            const isUpcoming = item.queueStatus?.toLowerCase() === 'upcoming'

            // remove ONLY cancelled upcoming items
            return !(isCancelled && isUpcoming)
          })
        }

        // 5. Re-run processing
        state.queue = processQueue(mergedArray)
      })

      .addCase(pollLobbyQueueAsync.rejected, (state, action) => {
        state.pollLobbyQueueStatus = APIStatus.rejected
        state.pollLobbyQueueError = action.payload || 'Failed to fetch lobby queue'
        // Don't update timestamp on failure - will retry with same timestamp
        // lastPollTimestamp remains unchanged
      })

    //callInNextVisitAsync
    builder.addCase(callInNextVisitAsync.fulfilled, (state, action) => {
      state.callInNextVisitStatus = APIStatus.fulfilled
      state.queue.checkedIn = action.payload.queue
      state.currentVisit = action.payload.nextVisit
      if (state.priorityVisit && state.priorityVisit.id === action.payload.nextVisit?.id) {
        state.priorityVisit = null
      }
    })
    builder.addCase(callInNextVisitAsync.rejected, (state, action: any) => {
      state.callInNextVisitStatus = APIStatus.rejected
      if (action?.payload?.error === 'calledInTwice') {
        state.callInNextVisitError = action.payload.error
        state.queue = action.payload.queue
        if (action?.payload?.visitId === action?.payload?.priorityVisitId) {
          state.priorityVisit = null
        }
      }
    })
    builder.addCase(callInNextVisitAsync.pending, (state, action) => {
      state.callInNextVisitStatus = APIStatus.pending
    })

    //patientDetailsAsync
    builder.addCase(patientDetailsAsync.fulfilled, (state, action) => {
      state.patientDetailsStatus = APIStatus.fulfilled
      state.patientDetails = action.payload
    })
    builder.addCase(patientDetailsAsync.rejected, (state, action) => {
      state.patientDetailsStatus = APIStatus.rejected
      state.patientDetailsError = action.payload || 'Failed to fetch patient details'
    })
    builder.addCase(patientDetailsAsync.pending, (state, action) => {
      state.patientDetailsStatus = APIStatus.pending
    })

    //forceCheckInPatientAsync
    builder.addCase(forceCheckInPatientAsync.fulfilled, (state, action) => {
      state.forceCheckInPatientStatus = APIStatus.fulfilled
      state.forceCheckInPatient = action.payload

      // Find the visit in the queue and update its queueStatus from "upcoming" to "waiting"
      const visitId = action.payload.visitId
      const allQueueItems = [...state.queue.checkedIn, ...state.queue.upcoming, ...state.queue.previous]

      // Find and update the visit
      const updatedItems = allQueueItems.map((item) => {
        if (item.visitId === visitId && (item.queueStatus === 'upcoming' || item.queueStatus === 'UPCOMING')) {
          return {
            ...item,
            queueStatus: 'waiting',
          }
        }
        return item
      })

      // Re-process the queue to move items to correct buckets
      state.queue = processQueue(updatedItems)
    })
    builder.addCase(forceCheckInPatientAsync.rejected, (state, action) => {
      state.forceCheckInPatientStatus = APIStatus.rejected
    })
    builder.addCase(forceCheckInPatientAsync.pending, (state, action) => {
      state.forceCheckInPatientStatus = APIStatus.pending
    })

    //lobbyUndoVisitAsync

    builder.addCase(lobbyUndoVisitAsync.fulfilled, (state, action) => {
      state.lobbyUndoVisitStatus = APIStatus.fulfilled
      state.lobbyUndoVisit = action.payload.visitId

      // Find the visit in the queue and update its queueStatus from "waiting" to "upcoming"
      const visitId = action.payload.visitId
      const allQueueItems = [...state.queue.checkedIn, ...state.queue.upcoming, ...state.queue.previous]

      // Find and update the visit
      const updatedItems = allQueueItems.map((item) => {
        if (item.visitId === visitId && (item.queueStatus === 'waiting' || item.queueStatus === 'WAITING')) {
          return {
            ...item,
            queueStatus: 'upcoming',
            visit: {
              ...item.visit,
              status: 'booked',
            },
          }
        }
        return item
      })

      // Re-process the queue to move items to correct buckets
      state.queue = processQueue(updatedItems)
    })
    builder.addCase(lobbyUndoVisitAsync.rejected, (state, action) => {
      state.lobbyUndoVisitStatus = APIStatus.rejected
      state.lobbyUndoVisitError = (action.payload as string) || 'Failed to undo visit'
    })
    builder.addCase(lobbyUndoVisitAsync.pending, (state, action) => {
      state.lobbyUndoVisitStatus = APIStatus.pending
      state.lobbyUndoVisitError = null
    })

    //resetPriorityVisit upon successfully starting chiropractic session startChiropracticAsync or stopping chiropractic session stopChiropracticAsync
    builder.addCase(startChiropracticAsync.fulfilled, (state, action) => {
      if (state.priorityVisit && state.priorityVisit.id === action.payload.visit?.id) {
        state.priorityVisit = null
      }
    })

    builder.addCase(stopChiropracticAsync.fulfilled, (state, action) => {
      if (state.priorityVisit && state.priorityVisit.id === action.payload.visit?.id) {
        state.priorityVisit = null
      }
    })

    //reset priority visit and current visit upon successfully undoing visit from current patient screen
    builder.addCase(undoVisitAsync.fulfilled, (state, action) => {
      if (state.priorityVisit && state.priorityVisit.id === action.payload.visitId) {
        state.priorityVisit = null
      }
      if (state.currentVisit?.visitId === action.payload.visitId) {
        state.currentVisit = null
      }
    })

    // Scans async thunks
    builder.addCase(getAllScanAsync.fulfilled, (state, action) => {
      state.scansStatus = APIStatus.fulfilled
      state.scans = action.payload
    })
    builder.addCase(getAllScanAsync.rejected, (state, action) => {
      state.scansStatus = APIStatus.rejected
    })
    builder.addCase(getAllScanAsync.pending, (state, action) => {
      state.scansStatus = APIStatus.pending
    })

    builder.addCase(getScanByIdAsync.fulfilled, (state, action) => {
      state.scanByIdStatus = APIStatus.fulfilled
      state.scanById = action.payload
    })
    builder.addCase(getScanByIdAsync.rejected, (state, action) => {
      state.scanByIdStatus = APIStatus.rejected
    })
    builder.addCase(getScanByIdAsync.pending, (state, action) => {
      state.scanByIdStatus = APIStatus.pending
    })

    // Notes async thunks
    builder.addCase(getAllNotesIdAsync.fulfilled, (state, action) => {
      state.getAllNotesIdStatus = APIStatus.fulfilled
      state.getAllNotesId = Array.isArray(action.payload.response) ? action.payload.response : []
    })
    builder.addCase(getAllNotesIdAsync.rejected, (state, action) => {
      state.getAllNotesIdStatus = APIStatus.rejected
    })
    builder.addCase(getAllNotesIdAsync.pending, (state, action) => {
      state.getAllNotesIdStatus = APIStatus.pending
    })

    builder.addCase(getNoteByIdAsync.fulfilled, (state, action) => {
      state.noteByIdStatus = APIStatus.fulfilled
      state.noteById = action.payload.response as NoteById
    })
    builder.addCase(getNoteByIdAsync.rejected, (state, action) => {
      state.noteByIdStatus = APIStatus.rejected
      state.noteByIdError = action.payload as string
    })
    builder.addCase(getNoteByIdAsync.pending, (state, action) => {
      state.noteByIdStatus = APIStatus.pending
      state.noteByIdError = null
    })

    // Priority answers async thunk
    builder.addCase(priorityAnswersAsync.fulfilled, (state, action) => {
      state.priorityAnswersStatus = APIStatus.fulfilled
      state.priorityAnswers = action.payload
    })
    builder.addCase(priorityAnswersAsync.rejected, (state, action) => {
      state.priorityAnswersStatus = APIStatus.rejected
    })
    builder.addCase(priorityAnswersAsync.pending, (state, action) => {
      state.priorityAnswersStatus = APIStatus.pending
    })

    // Patient notes async thunk
    builder.addCase(postPatientNotesAsync.fulfilled, (state, action) => {
      state.postNotesStatus = APIStatus.fulfilled
      state.postNotes = action.payload
      if (state.patientDetails?.patient && action.payload.notes !== undefined) {
        state.patientDetails.patient.notes = action.payload.notes
      }
    })
    builder.addCase(postPatientNotesAsync.rejected, (state, action) => {
      state.postNotesStatus = APIStatus.rejected
    })
    builder.addCase(postPatientNotesAsync.pending, (state, action) => {
      state.postNotesStatus = APIStatus.pending
    })

    //postTreatmentPlanType - update patientDetails when treatment tracker is updated
    builder.addCase(postTreatmentPlanType.pending, (state) => {
      state.postTreatmentPlanTypeStatus = APIStatus.pending
      state.postTreatmentPlanTypeError = null
    })
    builder.addCase(postTreatmentPlanType.fulfilled, (state, action) => {
      state.postTreatmentPlanTypeStatus = APIStatus.fulfilled
      state.postTreatmentPlanTypeError = null
      // Update currentPatient treatmentTracker with the response
      if (state.patientDetails && action.payload) {
        state.patientDetails.treatmentTracker = action.payload
      }
    })
    builder.addCase(postTreatmentPlanType.rejected, (state, action) => {
      state.postTreatmentPlanTypeStatus = APIStatus.rejected
      state.postTreatmentPlanTypeError = action.payload as string
    })

    // Sync visit frequency update from currentPatientPortal
    // This ensures patientDetails in lobbyPortal stays in sync when visit frequency is updated
    builder.addCase(updateVisitFrequencyAsync.fulfilled, (state, action) => {
      if (state.patientDetails?.treatmentTracker?.data && action.payload) {
        state.patientDetails.treatmentTracker.data.visitFrequency = action.payload.visitFrequency
      }
    })
    builder.addCase(updateVisitFrequencyAsync.rejected, (state, action) => {
      // Revert optimistic update on error
      if (state.patientDetails?.treatmentTracker?.data && action.meta?.arg?.originalFrequency) {
        state.patientDetails.treatmentTracker.data.visitFrequency = action.meta.arg.originalFrequency
      }
    })

    // Sync next appointment day update from currentPatientPortal
    // Note: nextAppointmentDay is stored in currentPatientPortal state, not in patientDetails object
    // PatientDetailScreen reads from currentPatientPortal.nextAppointmentDay directly

    builder.addCase(createTempOtpAsync.pending, (state) => {
      state.tempOtpStatus = APIStatus.pending
      state.tempOtpError = null
    })
    builder.addCase(createTempOtpAsync.fulfilled, (state) => {
      state.tempOtpStatus = APIStatus.fulfilled
      state.tempOtpError = null
    })
    builder.addCase(createTempOtpAsync.rejected, (state, action) => {
      state.tempOtpStatus = APIStatus.rejected
      state.tempOtpError = action.payload || 'Failed to reset OTP'
    })
  },
})

export const {
  setPriorityVisit,
  resetPriorityVisit,
  resetLobbyState,
  clearLobby,
  resetLobbyStatus,
  resetLobbyError,
  resetLobbyStatusAndError,
  resetCallInNextVisitStatus,
  setCurrentVisit,
  setLastPollTimestamp,
  resetLastPollTimestamp,
  setRefreshDate,
  resetForceCheckInPatientStatus,
  openResetOtpModal,
  resetTempOtpStatus,
  resetPatientDetailsStatus,
  setPatientNotes,
  updateResponseOptionPriority,
  clearResponseOptionPriority,
  clearNoteById,
  clearScans,
  resetNotesListAndNoteById,
  updateTreatmentTracker,
} = lobbyPortalSlice.actions

export default lobbyPortalSlice.reducer
